// Deal next-step data layer (NBA shock package, batch 2).
//
// The card is its own server state, not part of the deal payload: it must
// refresh after every executed action, while a failing next-step read must
// never block the deal mutations that invalidate the `['deals', …]` prefix.
// All keys use their own roots for the same reason.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NbaActionType } from '../../../sales-health/nba-engine.js';
import { ApiError } from '../../../shared/api-client/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  executeDealNextStep,
  getDealNextStep,
  getDealNextStepQueryKey,
  getDealWhatIfQueryKey,
  getDealWinProbabilityQueryKey,
  simulateDealWhatIf,
  type DealNextStepExecutionResult,
  type DealWhatIfProjection,
  type ExecuteDealNextStepInput,
} from '../api/deals.client.js';

/**
 * A stale action is the server's honest "the deal moved on since you read the
 * card". The API maps `ConflictError('STALE_ACTION')` to status 409, so the
 * status is the contract — the error `code` stays generic (`conflict`).
 */
export function isStaleNextStepError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/** The single best recommendation for the deal cockpit. */
export function useDealNextStep(dealId: string) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  return useQuery({
    queryKey: getDealNextStepQueryKey(workspaceId, dealId),
    queryFn: () => getDealNextStep(dealId),
    enabled: scope !== null,
    // jsdom tests and offline shells have no API; a failed read renders the
    // error block instead of retrying behind the user's back.
    retry: false,
  });
}

/**
 * What-if projection of one action type. Mounts only when a type is selected:
 * the endpoint is a pure calculation, but there is nothing to show before the
 * user picks an action.
 */
export function useDealWhatIf(dealId: string, type: NbaActionType | null) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  return useQuery<DealWhatIfProjection>({
    queryKey: getDealWhatIfQueryKey(workspaceId, dealId, type),
    queryFn: () => {
      if (type === null) throw new Error('what-if requires an action type');
      return simulateDealWhatIf(dealId, type);
    },
    enabled: scope !== null && type !== null,
    retry: false,
  });
}

/**
 * Executes a confirmed `server` action.
 *
 * After success the deal, its win probability, the card and the dashboard
 * attention queue refetch — the visible probability number and the ranking must
 * move, not just the toast. A 409 means the action is already done or no longer
 * applies, so the card refreshes quietly: showing "failed" for work another
 * surface completed would be a lie.
 */
export function useExecuteDealNextStep(dealId: string) {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return useMutation<DealNextStepExecutionResult, Error, ExecuteDealNextStepInput>({
    mutationFn: (input) => executeDealNextStep(dealId, input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['hot-leads', workspaceId] }),
        // Prefix without `limit`: every mounted queue page must re-rank, because
        // the executed step changes this deal's money at risk.
        queryClient.invalidateQueries({ queryKey: ['deal-attention-queue', workspaceId] }),
        queryClient.invalidateQueries({
          queryKey: getDealWinProbabilityQueryKey(workspaceId, dealId),
        }),
        queryClient.invalidateQueries({
          queryKey: getDealNextStepQueryKey(workspaceId, dealId),
        }),
        queryClient.invalidateQueries({ queryKey: ['deal-what-if', workspaceId, dealId] }),
      ]);
    },
    onError: async (error) => {
      if (!isStaleNextStepError(error)) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getDealNextStepQueryKey(workspaceId, dealId),
        }),
        queryClient.invalidateQueries({ queryKey: ['deal-what-if', workspaceId, dealId] }),
        queryClient.invalidateQueries({
          queryKey: getDealWinProbabilityQueryKey(workspaceId, dealId),
        }),
      ]);
    },
  });
}
